import { useState, useRef } from 'react'
import { TIME_BUCKETS } from './chartTypes'
import InlineCkEditor from 'Shared/components/CKEditor/InlineCkEditor'

// Collect dimension IDs used by other dimension slots (to exclude from picker)
function usedDimIds(allSlots, allSlotValues, currentSlotKey) {
  const ids = []
  for (const [key, s] of Object.entries(allSlots)) {
    if (key === currentSlotKey || s.kind !== 'dimension') continue
    const val = allSlotValues[key]
    if (Array.isArray(val)) {
      val.forEach(d => ids.push(d.id || d))
    } else if (val?.id) {
      ids.push(val.id)
    }
  }
  return ids
}

export function SlotEditor({ slot, slotKey, value, allSlotValues, allSlots, onChange, queryInfo }) {
  if (slot.kind === 'html') {
    return <HtmlSlot slot={slot} value={value} onChange={onChange} />
  }
  if (slot.kind === 'time') {
    return <TimeDimensionSlot slot={slot} value={value} onChange={onChange} queryInfo={queryInfo} />
  }
  if (slot.kind === 'metric') {
    return (
      <MetricPicker
        label={slot.label}
        required={slot.required}
        single={slot.single}
        availableMetrics={queryInfo.availableMetrics}
        selectedIds={Array.isArray(value) ? value : value ? [value] : []}
        onToggle={(id) => {
          if (slot.single) {
            const arr = Array.isArray(value) ? value : value ? [value] : []
            onChange(arr.includes(id) ? arr.filter(x => x !== id) : [id])
          } else {
            const arr = Array.isArray(value) ? value : []
            onChange(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id])
          }
        }}
      />
    )
  }
  if (slot.kind === 'boolean') {
    return (
      <div className="query-builder__section">
        <label className="query-builder__checkbox">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          {slot.label}
        </label>
      </div>
    )
  }
  if (slot.kind === 'geoDimension') {
    const geoDims = queryInfo.availableDimensions.filter(d => d.geo)
    return (
      <div className="query-builder__section">
        <h3>{slot.label}</h3>
        <div className="query-builder__options">
          {geoDims.map(d => (
            <button
              key={d.id}
              type="button"
              className={`query-builder__option ${value?.id === d.id ? 'active' : ''}`}
              onClick={() => onChange(value?.id === d.id ? null : { id: d.id })}
            >
              {d.name || d.label || d.id}
            </button>
          ))}
          {geoDims.length === 0 && (
            <span style={{ color: '#94a3b8', fontSize: 13 }}>No geographic dimensions available for this data source</span>
          )}
        </div>
      </div>
    )
  }
  if (slot.kind === 'dimension') {
    const includeTime = !!slot.includeTime
    const excludeIds = usedDimIds(allSlots, allSlotValues, slotKey)
    let dims = includeTime ? queryInfo.availableDimensions : queryInfo.availableDimensions.filter(d => d.kind !== 'time')
    dims = dims.filter(d => !excludeIds.includes(d.id))
    return (
      <DimensionPicker
        label={slot.label}
        required={slot.required}
        single={slot.single}
        availableDimensions={dims}
        selectedDimensions={Array.isArray(value) ? value : value ? [value] : []}
        onToggle={(dimId) => {
          const dimInfo = queryInfo.availableDimensions.find(d => d.id === dimId)
          const newDim = { id: dimId }
          if (dimInfo?.kind === 'time') newDim.timeBucket = 'day'

          if (slot.single) {
            onChange(value?.id === dimId ? null : newDim)
          } else {
            const arr = Array.isArray(value) ? value : []
            const exists = arr.find(d => d.id === dimId)
            onChange(exists ? arr.filter(d => d.id !== dimId) : [...arr, newDim])
          }
        }}
        onUpdateTimeBucket={(dimId, bucket) => {
          if (slot.single) {
            onChange({ ...value, timeBucket: bucket })
          } else {
            const arr = Array.isArray(value) ? value : []
            onChange(arr.map(d => d.id === dimId ? { ...d, timeBucket: bucket } : d))
          }
        }}
      />
    )
  }
  return null
}

function TimeDimensionSlot({ slot, value, onChange, queryInfo }) {
  const timeDimensions = queryInfo.availableDimensions.filter(d => d.kind === 'time')

  if (timeDimensions.length === 0) return null

  const selected = value || (slot.required ? { id: timeDimensions[0].id, timeBucket: 'day' } : null)
  const dimInfo = selected && timeDimensions.find(d => d.id === selected.id)

  return (
    <div className="query-builder__section">
      <h3>{slot.label}{!slot.required && <span className="query-builder__optional"> (optional)</span>}</h3>
      <div className="query-builder__pills">
        {!selected && (
          <button
            type="button"
            className="query-builder__pill-add"
            onClick={() => onChange({ id: timeDimensions[0].id, timeBucket: 'day' })}
          >
            + Add time dimension
          </button>
        )}
        {selected && (
          <span className="query-builder__pill">
            {dimInfo?.name || dimInfo?.label || selected.id}
            <select
              className="query-builder__pill-select"
              value={selected.timeBucket || 'day'}
              onChange={(e) => onChange({ ...selected, timeBucket: e.target.value })}
            >
              {TIME_BUCKETS.map(b => (
                <option key={b} value={b}>{_.capitalize(b)}</option>
              ))}
            </select>
            {!slot.required && (
              <button type="button" onClick={() => onChange(null)}>&times;</button>
            )}
          </span>
        )}
        {selected && timeDimensions.length > 1 && (
          <select
            className="query-builder__pill-select"
            value={selected.id}
            onChange={(e) => onChange({ id: e.target.value, timeBucket: selected.timeBucket || 'day' })}
          >
            {timeDimensions.map(d => (
              <option key={d.id} value={d.id}>{d.name || d.label || d.id}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}

function MetricPicker({ label, required, single, availableMetrics, selectedIds, onToggle }) {
  const [showList, setShowList] = useState(false)
  const unselected = availableMetrics.filter(m => !selectedIds.includes(m.id))
  const canAdd = single ? selectedIds.length === 0 : unselected.length > 0

  return (
    <div className="query-builder__section">
      <h3>{label}{!required && <span className="query-builder__optional"> (optional)</span>}</h3>
      <div className="query-builder__pills">
        {selectedIds.map(id => {
          const m = availableMetrics.find(am => am.id === id)
          return (
            <span key={id} className="query-builder__pill">
              {m?.label || id}
              <button type="button" onClick={() => onToggle(id)}>&times;</button>
            </span>
          )
        })}
        {canAdd && !showList && (
          <button type="button" className="query-builder__pill-add" onClick={() => setShowList(true)}>
            + Add metric
          </button>
        )}
      </div>
      {showList && (
        <div className="query-builder__picker-list">
          {unselected.map(m => (
            <button
              key={m.id}
              type="button"
              className="query-builder__picker-item"
              onClick={() => {
                onToggle(m.id)
                if (single || unselected.length <= 1) setShowList(false)
              }}
            >
              {m.label || m.id}
            </button>
          ))}
          <button type="button" className="query-builder__picker-close" onClick={() => setShowList(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  )
}

function groupDimensions(availableDimensions) {
  const byGroup = {}
  availableDimensions.forEach(d => {
    const group = d.group || 'fields'
    if (!byGroup[group]) byGroup[group] = []
    byGroup[group].push(d)
  })
  const groupOrder = Object.keys(byGroup).sort((a, b) => {
    if (a === 'fields') return -1
    if (b === 'fields') return 1
    return a.localeCompare(b)
  })
  return { byGroup, groupOrder }
}

function dimLabel(d) {
  return d.name || d.label || d.id
}

function DimensionPicker({ label, required, single, availableDimensions, selectedDimensions, onToggle, onUpdateTimeBucket }) {
  const [showList, setShowList] = useState(false)
  const selectedIds = selectedDimensions.map(d => d.id || d)
  const unselected = availableDimensions.filter(d => !selectedIds.includes(d.id))
  const { byGroup, groupOrder } = groupDimensions(unselected)
  const canAdd = single ? selectedDimensions.length === 0 : unselected.length > 0

  return (
    <div className="query-builder__section">
      <h3>{label}{!required && <span className="query-builder__optional"> (optional)</span>}</h3>
      <div className="query-builder__pills">
        {selectedDimensions.map(sel => {
          const dimId = sel.id || sel
          const info = availableDimensions.find(d => d.id === dimId)
          return (
            <span key={dimId} className="query-builder__pill">
              {dimLabel(info || { id: dimId })}
              {info?.kind === 'time' && sel.timeBucket && (
                <select
                  className="query-builder__pill-select"
                  value={sel.timeBucket}
                  onChange={(e) => onUpdateTimeBucket(dimId, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {TIME_BUCKETS.map(b => (
                    <option key={b} value={b}>{_.capitalize(b)}</option>
                  ))}
                </select>
              )}
              <button type="button" onClick={() => onToggle(dimId)}>&times;</button>
            </span>
          )
        })}
        {canAdd && !showList && (
          <button type="button" className="query-builder__pill-add" onClick={() => setShowList(true)}>
            + Add dimension
          </button>
        )}
      </div>
      {showList && (
        <div className="query-builder__picker-list">
          {groupOrder.map(group => (
            <div key={group} className="query-builder__picker-group">
              <span className="query-builder__picker-group-label">{_.capitalize(group)}</span>
              {byGroup[group].map(d => (
                <button
                  key={d.id}
                  type="button"
                  className="query-builder__picker-item"
                  onClick={() => { onToggle(d.id); if (single) setShowList(false) }}
                >
                  {dimLabel(d)}
                </button>
              ))}
            </div>
          ))}
          <button type="button" className="query-builder__picker-close" onClick={() => setShowList(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  )
}

function HtmlSlot({ slot, value, onChange }) {
  const [sourceMode, setSourceMode] = useState(false)

  return (
    <div className="query-builder__section">
      <h3>{slot.label}</h3>
      <InlineCkEditor
        value={value || ''}
        onUpdate={onChange}
        sourceMode={sourceMode}
        setSourceMode={setSourceMode}
        hideMergeTags
        features={['sourceMode']}
        placeholder="Type content here..."
      />
    </div>
  )
}
